import { Car, TerrainGrid } from '@antification/core';
import { Camera } from './Camera';

/**
 * What a car looks like.
 *
 * Six flat quadrilaterals and a shadow, drawn from the interpolated position
 * between the cell a car left and the cell it is heading for. `CarManager` keeps
 * deciding where that is; since ADR-008 it no longer knows what colour the
 * windshield is.
 *
 * A truck is the same six quadrilaterals, longer and taller, in a livery nobody
 * chose. That similarity is the point rather than an economy: §5's transit traffic
 * is *"trucks it never ordered"*, and a vehicle that looked like a different kind
 * of object would be the interface telling a mayor which deaths on their roads to
 * attribute to somebody else. The only visible difference is that it is bigger.
 */
export class CarRenderer {
  render(ctx: CanvasRenderingContext2D, camera: Camera, grid: TerrainGrid, car: Car): void {
    // Interpolate position with wrap-around awareness
    const currentX = this.interpolateWithWrap(car.gridX, car.targetGridX, car.progress, grid.getCols());
    const currentY = this.interpolateWithWrap(car.gridY, car.targetGridY, car.progress, grid.getRows());

    const screen = camera.gridToScreen(currentX, currentY);
    const scale = camera.getScale();

    const x = screen.x;
    const y = screen.y;

    // Car dimensions. A truck is half again as long and half again as tall — big
    // enough to read at a glance, small enough to still be traffic.
    const truck = car.freight !== null;
    const carLength = (truck ? 27 : 18) * scale;
    const carWidth = (truck ? 12 : 10) * scale;
    const carHeight = (truck ? 9 : 6) * scale;

    ctx.save();
    ctx.globalAlpha = car.opacity;

    // Shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.beginPath();
    ctx.ellipse(x, y + 3 * scale, carLength / 2, carWidth / 3, 0, 0, Math.PI * 2);
    ctx.fill();

    // Car body (isometric box)
    // Bottom face
    ctx.fillStyle = this.darkenColor(car.color, 0.7);
    ctx.beginPath();
    ctx.moveTo(x - carLength / 2, y);
    ctx.lineTo(x, y + carWidth / 2);
    ctx.lineTo(x + carLength / 2, y);
    ctx.lineTo(x, y - carWidth / 2);
    ctx.closePath();
    ctx.fill();

    // Left side
    ctx.fillStyle = this.darkenColor(car.color, 0.85);
    ctx.beginPath();
    ctx.moveTo(x - carLength / 2, y);
    ctx.lineTo(x, y + carWidth / 2);
    ctx.lineTo(x, y + carWidth / 2 - carHeight);
    ctx.lineTo(x - carLength / 2, y - carHeight);
    ctx.closePath();
    ctx.fill();

    // Right side
    ctx.fillStyle = car.color;
    ctx.beginPath();
    ctx.moveTo(x + carLength / 2, y);
    ctx.lineTo(x, y + carWidth / 2);
    ctx.lineTo(x, y + carWidth / 2 - carHeight);
    ctx.lineTo(x + carLength / 2, y - carHeight);
    ctx.closePath();
    ctx.fill();

    // Top
    ctx.fillStyle = this.darkenColor(car.color, 1.1);
    ctx.beginPath();
    ctx.moveTo(x - carLength / 2, y - carHeight);
    ctx.lineTo(x, y - carWidth / 2 - carHeight);
    ctx.lineTo(x + carLength / 2, y - carHeight);
    ctx.lineTo(x, y + carWidth / 2 - carHeight);
    ctx.closePath();
    ctx.fill();

    // Windshield. A truck's cab is at one end rather than in the middle, which is
    // the whole of the silhouette difference.
    const glassX = truck ? x + carLength / 4 : x;
    const glassHalf = truck ? carLength / 8 : carLength / 4;
    ctx.fillStyle = 'rgba(135, 180, 220, 0.7)';
    ctx.beginPath();
    ctx.moveTo(glassX - glassHalf, y - carHeight - 1 * scale);
    ctx.lineTo(glassX, y - carWidth / 4 - carHeight - 1 * scale);
    ctx.lineTo(glassX + glassHalf, y - carHeight - 1 * scale);
    ctx.lineTo(glassX, y + carWidth / 4 - carHeight - 1 * scale);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  /**
   * Where a car is between two cells.
   *
   * A car that crosses the edge of the map reappears on the other side, and
   * animating that would send it skidding across the whole district. Anything
   * longer than half a map is a wrap, so it simply arrives.
   */
  private interpolateWithWrap(from: number, to: number, progress: number, size: number): number {
    let diff = to - from;

    // If moving more than half the grid, it's a wrap - skip animation
    if (Math.abs(diff) > size / 2) {
      // Just return the target position (no animation for wrap)
      return to;
    }

    // Normal interpolation for short distances
    return from + diff * progress;
  }

  private darkenColor(hex: string, factor: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    const newR = Math.min(255, Math.floor(r * factor));
    const newG = Math.min(255, Math.floor(g * factor));
    const newB = Math.min(255, Math.floor(b * factor));

    return `rgb(${newR}, ${newG}, ${newB})`;
  }
}
